// Market panel — three tabs (Listings / NPC Shop / History). Modal
// pattern matches WorkbenchUI: main.ts owns the M-key handler and
// setModal(true/false) around open/close; this class is visuals +
// state. __nexusDebug.state() reads via getters on this class. Step
// 15 shipped the shell; Step 16 adds the Listings body; Steps 17-18
// add NPC Shop + History bodies.

import { Application, Container, Graphics, Text } from "pixi.js";
import { balance } from "./types.ts";
import type { InventoryUI } from "./inventory-ui.ts";
import type { MarketView, PriceHistoryEntry } from "../../shared/market.ts";
import type { ClientMessage, InventorySlot, PlayerState, SkillEntry } from "../../shared/messages.ts";
import {
  buildGetMarketViewMessage,
  buildNpcBuyMessage,
  buildNpcSellMessage,
  buildRepairItemMessage,
} from "../../shared/messages.ts";
import { MarketHistoryTab } from "./market-history-tab.ts";
import { MarketListingsState } from "./market-listings-state.ts";
import { MarketListingsTab } from "./market-listings-tab.ts";
import { MarketNpcTab } from "./market-npc-tab.ts";
import { MarketRepairTab } from "./market-repair-tab.ts";
import {
  buildMarketTabStrip,
  TAB_STRIP_HEIGHT,
  type MarketTab,
} from "./market-tab-strip.ts";

// Client-side market state extends the wire MarketView with a local
// price-history cache populated by Step 19's getPriceHistory response
// handler. Wire shape stays MarketView; the broader type is what the
// MarketHost surfaces to tab renderers.
// Phase 6 Block C: remoteHistories caches cross-station price data
// keyed as remoteHistories[stationId][itemKey].
export type ClientMarketState = MarketView & {
  priceHistories: Record<string, readonly PriceHistoryEntry[]>;
  remoteHistories: Record<string, Record<string, readonly PriceHistoryEntry[]>>;
};

// Single wiring surface main.ts fills once via marketUI.setHost(...).
// Closures read clientState on demand so the panel always sees the
// latest snapshot without a push path. inventoryUI is a direct ref
// (selection-mode coupling needs the instance, not a closure).
export interface MarketHost {
  send: (msg: ClientMessage) => void;
  selfId: () => string | null;
  inventory: () => readonly InventorySlot[];
  credits: () => number;
  market: () => ClientMarketState;
  // Phase 6 Block C: skills snapshot for Market Analysis unlock check.
  skills: () => readonly SkillEntry[];
  inventoryUI: InventoryUI;
  // Phase 7 Block B — name resolution sources for the listings table.
  // Closures so the panel always reads the freshest snapshot.
  selfPlayer?: () => PlayerState | null;
  zonePlayers?: () => Map<string, PlayerState>;
}

const NOOP_HOST: MarketHost = {
  send: () => {},
  selfId: () => null,
  inventory: () => [],
  credits: () => 0,
  market: () => ({ listings: [], npcInventory: {}, npcPrices: [], fees: 0, priceHistories: {}, remoteHistories: {} }),
  skills: () => [],
  inventoryUI: null as unknown as InventoryUI,
};

export type { MarketTab } from "./market-tab-strip.ts";

// Layout constants — kept in sync with workbench-ui.ts so the two
// panels feel like part of the same family. If a balance.market.*
// block lands later, hoist these.
const PAD = 12; // contracts:allow-literal — inner padding px
const HEADER_H = 44; // contracts:allow-literal — title bar height px
const FOOTER_HINT_H = 24; // contracts:allow-literal — footer hint height px

const PANEL_BG = 0x12122a;
const PANEL_BORDER = 0x4455cc;
const PANEL_BORDER_W = 2; // contracts:allow-literal
const DIVIDER_COLOR = 0x3344aa;
const TITLE_COLOR = 0xeeeeff;
const MUTED_COLOR = 0x666688;
const REFRESH_COLOR = 0x8888aa;

const TITLE_SIZE = 18; // contracts:allow-literal
const BODY_SIZE = 11; // contracts:allow-literal

export class MarketUI {
  readonly root: Container;
  private readonly app: Application;
  readonly listingsTab = new MarketListingsTab();
  readonly npcTab = new MarketNpcTab();
  readonly historyTab = new MarketHistoryTab();
  readonly repairTab = new MarketRepairTab();
  // Listings state lives in its own class — see market-listings-state.ts
  // for the rationale on the asymmetry vs MarketNpcTab (which owns
  // its own state).
  readonly listingsState = new MarketListingsState({
    sendFn: (msg) => this.host.send(msg),
    setSelectedSlotInUI: (idx) => this.host.inventoryUI?.setSelectedSlot(idx),
    onChange: () => this.rebuildBody(),
  });

  private _isOpen = false;
  // Reset to 'listings' on every open() — UX-reversibility: change
  // open()'s assignment to skip the reset for last-tab-restored.
  private _activeTab: MarketTab = "listings";

  private host: MarketHost = NOOP_HOST;

  // Panel-body cache so we can rebuild ONLY the active tab body on
  // state changes, not the whole panel (tab strip, chrome, etc).
  private bodyContainer: Container | null = null;
  private bodyRect: { x: number; y: number; w: number; h: number } | null = null;
  private bodyPanel: Container | null = null;
  // Tab-strip tracked separately so tab-switches can rebuild it
  // (active highlight, bold label) without touching panel chrome.
  private tabStripContainer: Container | null = null;
  private tabStripRect: { x: number; y: number; w: number } | null = null;

  constructor(app: Application) {
    this.app = app;
    this.root = new Container();
    this.root.name = "market-panel";
    this.root.visible = false;
    this.root.zIndex = 200; // contracts:allow-literal — above inventory (100), peer with workbench
    app.stage.sortableChildren = true;
    app.stage.addChild(this.root);
  }

  // Main.ts wires once after clientState + NetworkClient + inventoryUI
  // are ready. Host closures are pulled on every rebuild, so later
  // mutations to clientState propagate without a separate push path.
  setHost(host: MarketHost): void { this.host = host; }

  get isOpen(): boolean { return this._isOpen; }
  get activeTab(): MarketTab { return this._activeTab; }
  get selectedSlotIndex(): number | null { return this.listingsState.selectedSlotIndex; }
  get listPrice(): number { return this.listingsState.listPrice; }
  get listPending(): boolean { return this.listingsState.listPending; }
  get pendingListingIds(): readonly string[] { return Array.from(this.listingsState.pendingListingIds); }

  // Step 19 hooks: server-response handlers call these to clear pending
  // flags after the round-trip lands. Listings hooks delegate to state;
  // NPC hooks delegate to the tab class (state lives there).
  clearListPending(): void { if (this.listingsState.clearListPending()) this.rebuildBody(); }
  clearListingPending(listingId: string): void {
    if (this.listingsState.clearListingPending(listingId)) this.rebuildBody();
  }
  clearNpcBuyPending(itemId: string): void { this.npcTab.clearBuyPending(itemId); }
  clearNpcSellPending(itemId: string): void { this.npcTab.clearSellPending(itemId); }
  clearRepairPending(rowid: number): void { this.repairTab.clearPending(rowid); }
  // Rebuild just the active tab body on snapshot updates.
  refresh(): void { this.rebuildBody(); }

  open(): void {
    this._activeTab = "listings";
    this.listingsState.reset();
    this.rebuild();
    this.root.visible = true;
    this._isOpen = true;
    this.applySelectionMode();
    // Phase 5 Step 19 — fetch a fresh snapshot on open. Replaces the
    // retired 3s background poll; the ensuing `marketView` envelope
    // flows through the standard reducer arm.
    this.requestFreshView();
  }

  // Manual refresh — bound to the header "Refresh" button. Background
  // polling was retired in Phase 5 Step 19; durable pushes handle the
  // seller / buyer side in real time, and this covers new listings
  // created by other players while the panel has been open.
  private requestFreshView(): void {
    this.host.send(buildGetMarketViewMessage());
  }

  close(): void {
    this.root.visible = false;
    this._isOpen = false;
    this.listingsTab.teardown(); this.npcTab.teardown(); this.repairTab.teardown();
    this.root.removeChildren();
    this.bodyContainer = null;
    this.bodyRect = null;
    this.bodyPanel = null;
    this.tabStripContainer = null;
    this.tabStripRect = null;
    this.host.inventoryUI?.setSelectionMode(null);
  }

  setActiveTab(tab: MarketTab): void {
    if (this._activeTab === tab) return;
    this._activeTab = tab;
    if (this._isOpen) {
      // Tab-switch skips chrome rebuild — only the tab-strip visuals
      // and active-tab body need to refresh. Step 18 Prefix B.
      this.rebuildTabStrip();
      this.rebuildBody();
      this.applySelectionMode();
    }
  }

  // Selection mode is enabled ONLY while the Listings tab is active.
  private applySelectionMode(): void {
    if (!this.host.inventoryUI) return;
    if (this._activeTab === "listings") {
      this.host.inventoryUI.setSelectionMode((idx, slot) => this.listingsState.handleInventorySelect(idx, slot));
      this.host.inventoryUI.setSelectedSlot(this.listingsState.selectedSlotIndex);
    } else {
      this.host.inventoryUI.setSelectionMode(null);
    }
  }

  // Full rebuild on open + tab-switch. Rebuilds panel chrome;
  // rebuildBody() updates the active tab body in place.
  private rebuild(): void {
    this.root.removeChildren();
    const sw = this.app.renderer.width;
    const sh = this.app.renderer.height;
    // Reuse workbench panel ratios so the two modals feel matched.
    const pw = Math.round(sw * balance.workbench.panelWidthRatio);
    const ph = Math.round(sh * balance.workbench.panelHeightRatio);
    const px = Math.round((sw - pw) / 2); // contracts:allow-literal — centering idiom
    const py = Math.round((sh - ph) / 2); // contracts:allow-literal — centering idiom
    const panel = new Container();
    panel.x = px; panel.y = py;
    this.root.addChild(panel);

    const backdrop = new Graphics().rect(0, 0, sw, sh).fill({ color: 0x000000, alpha: 0.5 }); // contracts:allow-literal
    backdrop.x = -px; backdrop.y = -py;
    panel.addChild(backdrop);
    panel.addChild(
      new Graphics()
        .roundRect(0, 0, pw, ph, 8) // contracts:allow-literal — corner radius
        .fill({ color: PANEL_BG })
        .stroke({ color: PANEL_BORDER, width: PANEL_BORDER_W }),
    );

    // Title + ESC hint
    const title = new Text({
      text: "Market",
      style: { fontSize: TITLE_SIZE, fill: TITLE_COLOR, fontFamily: "monospace", fontWeight: "bold" },
    });
    title.x = PAD;
    title.y = Math.round((HEADER_H - title.height) / 2); // contracts:allow-literal
    panel.addChild(title);

    const escHint = new Text({
      text: "[ESC] Close",
      style: { fontSize: BODY_SIZE, fill: MUTED_COLOR, fontFamily: "monospace" },
    });
    escHint.x = pw - PAD - escHint.width;
    escHint.y = Math.round((HEADER_H - escHint.height) / 2); // contracts:allow-literal
    panel.addChild(escHint);

    // Phase 5 Step 19 — manual "Refresh" button, placed to the left of
    // the ESC hint. Needed because background polling was retired;
    // other-player-created listings won't auto-appear without this.
    const refresh = new Text({
      text: "[ Refresh ]",
      style: { fontSize: BODY_SIZE, fill: REFRESH_COLOR, fontFamily: "monospace" },
    });
    refresh.eventMode = "static";
    refresh.cursor = "pointer";
    refresh.on("pointerdown", () => this.requestFreshView());
    refresh.x = escHint.x - refresh.width - PAD;
    refresh.y = Math.round((HEADER_H - refresh.height) / 2); // contracts:allow-literal
    panel.addChild(refresh);

    // Header divider
    panel.addChild(new Graphics().rect(0, HEADER_H, pw, 1).fill({ color: DIVIDER_COLOR })); // contracts:allow-literal

    // Tab strip (extracted helper — market-tab-strip.ts)
    this.tabStripRect = { x: 0, y: HEADER_H + 1, w: pw }; // contracts:allow-literal — 1px header divider
    this.renderTabStrip(panel);

    // Tab-strip divider
    panel.addChild(
      new Graphics()
        .rect(0, HEADER_H + 1 + TAB_STRIP_HEIGHT, pw, 1) // contracts:allow-literal
        .fill({ color: DIVIDER_COLOR }),
    );

    // Body region — dispatched to the active tab renderer.
    const bodyY = HEADER_H + 1 + TAB_STRIP_HEIGHT + 1; // contracts:allow-literal — 1px tab divider
    const bodyH = ph - bodyY - FOOTER_HINT_H;
    this.bodyRect = { x: 0, y: bodyY, w: pw, h: bodyH };
    this.bodyPanel = panel;
    this.rebuildBody();

    // Footer hint
    const footerText = new Text({
      text: "[M] or [ESC] to close",
      style: { fontSize: BODY_SIZE, fill: MUTED_COLOR, fontFamily: "monospace" },
    });
    footerText.x = PAD;
    footerText.y = ph - FOOTER_HINT_H + Math.round((FOOTER_HINT_H - footerText.height) / 2); // contracts:allow-literal
    panel.addChild(footerText);
  }

  // Re-renders only the tab strip (tab-switch path). bodyPanel is
  // the same parent rebuild() used, so the strip lands at the same
  // coordinates regardless of which path got here.
  private rebuildTabStrip(): void {
    if (!this.bodyPanel || !this.tabStripRect) return;
    this.renderTabStrip(this.bodyPanel);
  }

  private renderTabStrip(parent: Container): void {
    if (!this.tabStripRect) return;
    if (this.tabStripContainer) {
      this.tabStripContainer.parent?.removeChild(this.tabStripContainer);
      this.tabStripContainer.destroy({ children: true });
      this.tabStripContainer = null;
    }
    this.tabStripContainer = buildMarketTabStrip({
      parent,
      x: this.tabStripRect.x,
      y: this.tabStripRect.y,
      activeTab: this._activeTab,
      onTabClick: (tab) => this.setActiveTab(tab),
    });
  }

  // Dispatches to the active tab renderer. Step 18 added the Repair tab.
  private rebuildBody(): void {
    if (!this.bodyPanel || !this.bodyRect) return;
    const { x, y, w, h } = this.bodyRect;
    this.listingsTab.teardown(); this.npcTab.teardown();
    this.historyTab.teardown(); this.repairTab.teardown();
    if (this.bodyContainer) {
      this.bodyContainer.parent?.removeChild(this.bodyContainer);
      this.bodyContainer.destroy({ children: true });
      this.bodyContainer = null;
    }

    const market = this.host.market();
    if (this._activeTab === "listings") {
      const s = this.listingsState;
      const bounds = s.priceBounds();
      this.listingsTab.build(this.bodyPanel, x, y, w, h, {
        listings: market.listings,
        selfId: this.host.selfId(),
        selectedSlot: s.selectedSlot,
        listPrice: s.listPrice,
        listPending: s.listPending,
        pendingListingIds: s.pendingListingIds,
        priceMin: bounds.min,
        priceMax: bounds.max,
        selfPlayer: this.host.selfPlayer?.() ?? null,
        zonePlayers: this.host.zonePlayers?.() ?? new Map<string, PlayerState>(),
      }, {
        onBuy: (id) => s.handleBuy(id),
        onCancel: (id) => s.handleCancel(id),
        onList: (itemId, price) => s.handleList(itemId, price),
        onPriceDelta: (d) => s.handlePriceDelta(d),
      });
      return;
    }

    if (this._activeTab === "npc") {
      this.npcTab.build(this.bodyPanel, x, y, w, h, {
        npcInventory: market.npcInventory,
        npcPrices: market.npcPrices,
        playerInventory: this.host.inventory(),
        playerCredits: this.host.credits(),
      }, {
        onBuy: (itemId, qty) => this.host.send(buildNpcBuyMessage(itemId, qty)),
        onSell: (itemId, qty) => this.host.send(buildNpcSellMessage(itemId, qty)),
      });
      return;
    }

    if (this._activeTab === "history") {
      const marketAnalysisXp = this.host.skills().find((s) => s.skillId === "market-analysis")?.xp ?? 0;
      this.historyTab.build(this.bodyPanel, x, y, w, h, {
        priceHistories: market.priceHistories,
        remoteHistories: market.remoteHistories,
        remoteMarketEnabled: marketAnalysisXp >= balance.skills.tierThresholds.journeyman.min,
      }, {
        send: (msg) => this.host.send(msg),
      });
      return;
    }

    if (this._activeTab === "repair") {
      this.repairTab.build(this.bodyPanel, x, y, w, h, {
        inventory: this.host.inventory(),
        playerCredits: this.host.credits(),
      }, {
        onRepair: (rowid) => this.host.send(buildRepairItemMessage(rowid)),
      });
      return;
    }
  }
}
